import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Select,
    MenuItem,
    Avatar,
    Paper,
    Button
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getMediaUrl } from '../../utils/urlHelper';
import { GET_All_EMPLOYEE_DETAILS } from '../../api/index';
import { SELECTED_MENU_PROPS } from '../../components/StyledComponents/StyledComponent';

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const BirthdayWidget = () => {
    const [filter, setFilter] = useState('Today');
    const [page, setPage] = useState(0);
    const itemsPerPage = 3;

    const { data: emp, isLoading, isError } = useQuery({
        queryKey: ['employeeListDetailedForBirthdays'],
        queryFn: GET_All_EMPLOYEE_DETAILS,
        staleTime: 1000 * 60 * 5,
    });

    const today = new Date();
    const currentMonth = today.getMonth() + 1; // 1-12
    const currentDay = today.getDate(); // 1-31

    const birthdays = useMemo(() => {
        let todayList = [];
        let upcomingList = [];

        if (!Array.isArray(emp)) return { today: [], upcoming: [] };

        emp.forEach((employee) => {
            // Try common DOB field names
            const dobStr = employee.date_of_birth || employee.dob || employee.DOB;
            if (!dobStr) return;

            const parts = dobStr.split(/[-/]/); // Support both - and /
            let empMonth, empDay;

            if (parts.length === 3) {
                // Determine format
                if (parts[0].length === 4) {
                    // YYYY-MM-DD
                    empMonth = parseInt(parts[1], 10);
                    empDay = parseInt(parts[2], 10);
                } else if (parseInt(parts[1], 10) > 12) {
                    // DD-YYYY-MM (unlikely) or DD-MM-YYYY (but month is middle)
                    // Wait, if parts[1] > 12, then parts[1] must be the day or year.
                    // Let's stick to common ones.
                    empMonth = parseInt(parts[0], 10);
                    empDay = parseInt(parts[1], 10);
                } else {
                    // Default to MM-DD-YYYY or DD-MM-YYYY
                    // We'll trust common Indian format DD-MM-YYYY if not YYYY-MM-DD
                    // But wait, the user's Greetings logic used split("-") and matched MM, DD.
                    empMonth = parseInt(parts[1], 10);
                    empDay = parseInt(parts[0], 10);
                }
            }

            if (isNaN(empMonth) || isNaN(empDay)) return;

            // Normalize month/day ranges
            if (empMonth > 12 && empDay <= 12) {
                // Swapped
                [empMonth, empDay] = [empDay, empMonth];
            }

            if (empMonth < 1 || empMonth > 12) return;

            const isToday = (empMonth === currentMonth && empDay === currentDay);

            let isUpcoming = false;
            if (!isToday) {
                const bdayDate = new Date(today.getFullYear(), empMonth - 1, empDay);
                let diffInDays = Math.ceil((bdayDate.getTime() - today.getTime()) / (1000 * 3600 * 24));

                // If it passed this year, check next year
                if (diffInDays < 0) {
                    const nextYearBday = new Date(today.getFullYear() + 1, empMonth - 1, empDay);
                    diffInDays = Math.ceil((nextYearBday.getTime() - today.getTime()) / (1000 * 3600 * 24));
                }

                if (diffInDays > 0 && diffInDays <= 182) { // Roughly 6 months
                    isUpcoming = true;
                }
            }

            const bdayObj = {
                ...employee,
                displayDate: `${monthNames[empMonth - 1]} ${empDay}`,
                sortVal: empMonth * 100 + empDay
            };

            if (isToday) todayList.push(bdayObj);
            if (isUpcoming) upcomingList.push(bdayObj);
        });

        // Unique sort for upcoming
        upcomingList.sort((a, b) => {
            const currentVal = currentMonth * 100 + currentDay;
            let valA = a.sortVal;
            let valB = b.sortVal;
            if (valA < currentVal) valA += 1200;
            if (valB < currentVal) valB += 1200;
            return valA - valB;
        });

        return { today: todayList, upcoming: upcomingList };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emp, currentMonth, currentDay]);

    const displayList = filter === 'Today' ? birthdays.today : birthdays.upcoming;
    const totalPages = Math.ceil(displayList.length / itemsPerPage);
    const paginatedList = displayList.slice(page * itemsPerPage, (page + 1) * itemsPerPage);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2, // Reduced corner radius
                background: '#fff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                border: '1px solid #f3f4f6',
                height: 'fit-content', // Height reduced to content
                width: '100%',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={800} color="#111827" sx={{ fontSize: '1rem' }}>
                    Birthday
                </Typography>
            </Box>

            <Select
                value={filter}
                onChange={(e) => { setFilter(e.target.value); setPage(0); }}
                size="small"
                fullWidth
                MenuProps={SELECTED_MENU_PROPS}
                sx={{
                    mb: 3,
                    borderRadius: 3,
                    backgroundColor: '#f9fafb',
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    fontWeight: 500,
                    color: '#4b5563',
                }}
            >
                <MenuItem value="Today">Today</MenuItem>
                <MenuItem value="Upcoming">Upcoming</MenuItem>
            </Select>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: '200px' }}>
                {isLoading ? (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontSize="0.8rem">Loading...</Typography>
                    </Box>
                ) : isError ? (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="error" fontSize="0.8rem">Error</Typography>
                    </Box>
                ) : paginatedList.length > 0 ? (
                    paginatedList.map((person, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: idx !== paginatedList.length - 1 ? '1px solid #f3f4f6' : 'none',
                                pb: idx !== paginatedList.length - 1 ? 1.5 : 0,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                    src={getMediaUrl(person.photo)}
                                    sx={{ width: 36, height: 36, bgcolor: '#f4f4f5', color: '#6366f1', fontWeight: 600, fontSize: '0.9rem' }}
                                >
                                    {person.name?.charAt(0)}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={700} color="#111827" sx={{ fontSize: '0.85rem' }}>
                                        {person.name || 'Employee'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        {person.displayDate} - Happy Birthday
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ fontSize: '1rem', pb: 1 }}>
                                🎉
                            </Box>
                        </Box>
                    ))
                ) : (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                            No birthdays {filter.toLowerCase()}.
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                    <Button
                        disabled={page === 0}
                        onClick={() => setPage(p => p - 1)}
                        size="small"
                        sx={{ minWidth: 30, height: 24, p: 0, textTransform: 'none', fontSize: '0.7rem' }}
                    >
                        Prev
                    </Button>
                    <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary', fontSize: '0.7rem' }}>
                        {page + 1} / {totalPages}
                    </Typography>
                    <Button
                        disabled={page === totalPages - 1}
                        onClick={() => setPage(p => p + 1)}
                        size="small"
                        sx={{ minWidth: 30, height: 24, p: 0, textTransform: 'none', fontSize: '0.7rem' }}
                    >
                        Next
                    </Button>
                </Box>
            )}
        </Paper>
    );
};

export default BirthdayWidget;
